import ShapeSceneAbstract from "./ShapeSceneAbstract";
import ShapeUtils from "./ShapeUtils";
import * as THREE from "three";
import * as CANNON from "cannon-es"; // https://github.com/pmndrs/cannon-es
import eventListenerOption from '../../lib/eventListenerOption';
import randomNumber from "../../lib/randomNumber";
import onDocumentLoaded from '../../lib/onDocumentLoaded';

type CannonCollideEvent = {
	body: CANNON.Body
	contact: CANNON.ContactEquation
	target: CANNON.Body
	type: string //"collide"
};

const yellowColor = new THREE.Color(0xFCEE1E);

/**
 * After constructing an instance you must call startRendering() on the instance
 * to start rendering.
 */
export default class ShapeAnimation extends ShapeSceneAbstract {
	private solidYellowMaterial: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ color: yellowColor });
	private meshGrabbedMaterial: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });

	private outlineMaterial: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ color: yellowColor, side: THREE.BackSide }); // Only render backside
	private outlineScalar = 1.05;

	private physicsWorld: CANNON.World;
	private shapes: {
		physicsBody: CANNON.Body,
		mesh: THREE.Mesh,
		outlineMesh: THREE.Mesh,
		physicsBodySecondary?: CANNON.Body,
		meshSecondary?: THREE.Mesh,
	}[];

	private static physicsGroups = {
		walls: 1,
		spheres: 2,
		toruses: 4,
		torusesBounceAreas: 8,
		flatYellows: 16
	};

	private flatCircles: THREE.Mesh[];
	private flatCirclePositions: {screen: {x: number, y: number},	offset?: {x: number,	y: number}}[] = [ // Relative to viewport (-1 to 0 to 1)
		{screen: {x: -1,	y: 0.65},	offset: {x: -0.55,	y: 0}},
		{screen: {x: 1,		y: 1},		offset: {x: 0.1,	y: 0.25}},
		{screen: {x: -0.15,	y: 0.15}},
		{screen: {x: -0.6,	y: -1},		offset: {x: 0,		y: 0.2}},
		{screen: {x: 1,		y: -0.55},	offset: {x: -0.2,	y: 0}}
	];
	private planeForFlatCircles: THREE.Mesh;

	private room: THREE.Mesh;
	private roomsPhysicalWalls: {
		floor?: CANNON.Body,
		ceiling?: CANNON.Body,
		left?: CANNON.Body,
		right?: CANNON.Body,
		front?: CANNON.Body,
		back?: CANNON.Body
	} = {};

	private raycaster = new THREE.Raycaster();

	private pointerPosition: {x: number, y: number} = null;
	private pointerGrabObjectConstraintBody: CANNON.Body;
	private pointerGrabObjectConstraintMesh: THREE.Mesh = null;
	private pointerGrabObjectConstraintPlane: THREE.Mesh;
	private pointerGrabObjectConstraint: CANNON.PointToPointConstraint
	private pointerGrabObjectConstraintTimeoutId: number;

	private pointerMoveLastPush: {
		object: THREE.Object3D | null,
		timestamp: number
   } = {object: null, timestamp: 0};


	static init() {
		onDocumentLoaded(document, () => {
			const elements = document.querySelectorAll<HTMLElement>(".shapeAnimation");
			elements.forEach((element) => {
				const shapeAnimation = new ShapeAnimation(
					element,
					"shapeAnimation__canvas"
				);
				shapeAnimation.startRendering();
			})
		});
	}


	constructor(
		htmlContainer: HTMLElement,
		canvasStyleClass: string,
		getCanvasSize?: () => {width: number, height: number}
	) {
		super(htmlContainer, canvasStyleClass,getCanvasSize);

		// Setup physics world
		this.physicsWorld = new CANNON.World();
		this.physicsWorld.gravity.set(0, 0, 20);
		
		// Create room to physically contain the objects
		this.createPhysicalRoom();

		// Mouse/pointer events
		document.addEventListener("pointermove", this.onPointerMove, eventListenerOption.passiveNotCapture);
		document.addEventListener("pointerdown", this.onPointerDown, eventListenerOption.passiveNotCapture);
		document.addEventListener("pointerup", this.onPointerUp, eventListenerOption.passiveNotCapture);

		// Disable actions when an object is grabbed
		document.addEventListener("touchmove", this.disableTouchMove, { passive:false });
		window.addEventListener("selectstart", this.disableSelect);
	}

	private onPointerMove = (event: PointerEvent) => {

		if (event.pointerType === "mouse") {
			// For use with hover effect
			this.pointerPosition = {x: event.clientX, y: event.clientY};
		} else {
			this.pointerPosition = null;
		}

		if (
			this.pointerGrabObjectConstraint !== undefined
			&& this.pointerGrabObjectConstraintBody !== undefined
			&& this.pointerGrabObjectConstraintPlane !== undefined
		) {

			const intersectsOnConstraintPlane = this.getPointerIntersectObjects(event.clientX, event.clientY, this.pointerGrabObjectConstraintPlane);
			if (intersectsOnConstraintPlane !== undefined && intersectsOnConstraintPlane.length > 0) {
				const intersect = intersectsOnConstraintPlane[0];
				this.pointerGrabObjectConstraintBody.position.set(intersect.point.x, intersect.point.y, intersect.point.z);
				this.pointerGrabObjectConstraint.update();
			}

			this.pointerMoveLastPush.timestamp = window.performance.now();
			this.pointerMoveLastPush.object = null;

		} else {

			// Shape interest work
			const intersects = this.getPointerIntersectObjects(event.clientX, event.clientY);
			if (intersects.length > 0) {
				const intersect = intersects[0];

				// Give the obect a push if we have hovered over it for more than XX ms
				const nowTimestamp = window.performance.now();
				if (intersect.object === this.pointerMoveLastPush.object) {
					if (nowTimestamp - this.pointerMoveLastPush.timestamp > 500) {
						const r = this.giveMeshPushAtIntersect(intersect, new THREE.Vector3(0, 0, 200)); // Pull towards camera
						if (r !== undefined && r.physicsBody !== undefined) {
							r.physicsBody.angularVelocity.set(0,0,0); // stop rotation.
						}
						this.pointerMoveLastPush.timestamp = nowTimestamp;
					}
				} else {
					this.pointerMoveLastPush.timestamp = nowTimestamp;
					this.pointerMoveLastPush.object = intersect.object;
				}
			} else {
				this.pointerMoveLastPush.object = null;
			}

		}
	}

	private onPointerDown = (event: PointerEvent) => {
		const intersects = this.getPointerIntersectObjects(event.clientX, event.clientY);
		if (intersects.length > 0) {
			const intersect = intersects[0];
			const meshAndBody = this.shapes.find(entry => intersect.object === entry.mesh);
			if (meshAndBody !== undefined) {

				if (this.pointerGrabObjectConstraintBody === undefined) {
					this.pointerGrabObjectConstraintBody = new CANNON.Body({ mass: 0 })
					this.pointerGrabObjectConstraintBody.addShape(new CANNON.Sphere(0.1))
					this.pointerGrabObjectConstraintBody.collisionFilterGroup = 0
					this.pointerGrabObjectConstraintBody.collisionFilterMask = 0
					this.physicsWorld.addBody(this.pointerGrabObjectConstraintBody);
				}

				if (this.pointerGrabObjectConstraintPlane === undefined) {
					this.pointerGrabObjectConstraintPlane = new THREE.Mesh(
						new THREE.PlaneBufferGeometry(100, 100),
						new THREE.MeshBasicMaterial({color: 0x0000ff, side: THREE.DoubleSide, wireframe: true})
					);
					this.pointerGrabObjectConstraintPlane.visible = false; // Hide it
					this.scene.add(this.pointerGrabObjectConstraintPlane);
				}

				this.movePointerGrabObjectConstraintPlane(intersect.point);

				if (this.pointerGrabObjectConstraint !== undefined) {
					this.physicsWorld.removeConstraint(this.pointerGrabObjectConstraint);
					this.pointerGrabObjectConstraint = undefined;
				}

				this.pointerGrabObjectConstraintMesh = meshAndBody.mesh;
				clearTimeout(this.pointerGrabObjectConstraintTimeoutId);
				//@ts-ignore
				this.pointerGrabObjectConstraintTimeoutId = setTimeout(
					() => {
						if (this.pointerGrabObjectConstraintMesh !== null) {
							this.setRevertableMeshMaterial(this.pointerGrabObjectConstraintMesh, this.meshGrabbedMaterial);
							this.pointerGrabObjectConstraint = this.addJointConstraint(
								meshAndBody.mesh,
								meshAndBody.physicsBody,
								new CANNON.Vec3(intersect.point.x, intersect.point.y, intersect.point.z),
								this.pointerGrabObjectConstraintBody,
							);
						}
					},
					100
				);
			}
		}
	}

	private onPointerUp = (event: PointerEvent) => {
		if (this.pointerGrabObjectConstraint !== undefined) {
			this.physicsWorld.removeConstraint(this.pointerGrabObjectConstraint);
			this.pointerGrabObjectConstraint = undefined;
		}
		if (this.pointerGrabObjectConstraintMesh !== null) {
			this.revertMeshMaterial(this.pointerGrabObjectConstraintMesh);
			this.pointerGrabObjectConstraintMesh = null;
		}

		const intersects = this.getPointerIntersectObjects(event.clientX, event.clientY);
		if (intersects.length > 0) {
			const intersect = intersects[0];
			this.giveMeshPushAtIntersect(intersect, new THREE.Vector3(0, 0, -2000));
		}

		this.pointerPosition = null;
	}

	private disableTouchMove = (event: Event) => {
		if (this.pointerGrabObjectConstraintMesh !== null) {
			event.preventDefault();
		}
	}
	private disableSelect = (event: Event) => {
		if (this.pointerGrabObjectConstraintMesh !== null) {
			event.preventDefault();
		}
	}


	private movePointerGrabObjectConstraintPlane(point: THREE.Vector3) {
		// Center at mouse position
		this.pointerGrabObjectConstraintPlane.position.copy(point)

		// Make it face toward the camera
		this.pointerGrabObjectConstraintPlane.quaternion.copy(this.camera.quaternion)
	}
	
	private addJointConstraint(constrainedMesh: THREE.Mesh, constrainedBody: CANNON.Body, position: CANNON.Vec3, jointBody: CANNON.Body): CANNON.PointToPointConstraint {
		
		let pivot: CANNON.Vec3 = new CANNON.Vec3(0, 0, 0);
		/* TODO: Remove. Setting to just 0 seemed to work better for both shapes.
		if (constrainedMesh.geometry.type === "TorusGeometry") {
			// Vector that goes from the body to the clicked point
			const vector = new CANNON.Vec3().copy(position).vsub(constrainedBody.position);

			// Apply anti-quaternion to vector to tranform it into the local body coordinate system
			const antiRotation = constrainedBody.quaternion.inverse();
			pivot = antiRotation.vmult(vector); // pivot is not in local body coordinates
		} else {
			pivot = new CANNON.Vec3(0, 0, 0);
		}
		*/

		// Move the cannon click marker body to the click position
        jointBody.position.copy(position);

		// Create a new constraint
		// The pivot for the jointBody is zero
		const jointConstraint = new CANNON.PointToPointConstraint(
			constrainedBody,
			pivot,
			jointBody,
			new CANNON.Vec3(0, 0, 0)
		);

		// Add the constraint to world
		this.physicsWorld.addConstraint(jointConstraint);

		return jointConstraint;
	}


	private setRevertableMeshMaterial(mesh: THREE.Mesh, material: THREE.Material) {
		if (material !== mesh.material) {
			if (mesh.userData.old_material === undefined) {
				mesh.userData.old_material = mesh.material;
			}
			mesh.material = material;
		}
	}
	private revertMeshMaterial(mesh: THREE.Mesh) {
		if (mesh !== null && mesh !== undefined && mesh.userData.old_material !== undefined) {
			mesh.material = mesh.userData.old_material;
			mesh.userData.old_material = undefined;
		}
	}


	private giveMeshPushAtIntersect(
		intersect: THREE.Intersection,
		worldPush: THREE.Vector3,
		meshAndBody?: { physicsBody: CANNON.Body, mesh: THREE.Mesh}
	): {physicsBody: CANNON.Body, mesh: THREE.Mesh} {
		if (meshAndBody === undefined) {
			meshAndBody = this.shapes.find(entry => intersect.object === entry.mesh);
		}
		if (meshAndBody === undefined || meshAndBody.mesh === undefined) {
			return meshAndBody;
		}

		const impulsePush = meshAndBody.mesh.worldToLocal(worldPush);

		const facesPoint = new THREE.Vector3(intersect.face.normal.x, intersect.face.normal.y, intersect.face.normal.z);

		let impulse = new CANNON.Vec3(
			(facesPoint.x * -20 + impulsePush.x),
			(facesPoint.y * -20 + impulsePush.y),
			(facesPoint.z * -20 + impulsePush.z)
		);

		meshAndBody.physicsBody.applyLocalImpulse(
			impulse,
			new CANNON.Vec3(facesPoint.x, facesPoint.y, facesPoint.z).scale(0.33)
		);

		return meshAndBody; // Jump out of for loop
	}

	private getPointerIntersectObjects(mouseX: number, mouseY: number, objects: THREE.Object3D | THREE.Object3D[] = this.shapesContainer.children): THREE.Intersection[] {
		const rendererRect = this.renderer.domElement.getBoundingClientRect();

		// Adjust mouse click position to be relative to the render
		const adjMouseX = mouseX - rendererRect.left;
		const adjMouseY = mouseY - rendererRect.top;

		// Get 3D point form the client x y
		const mouse = new THREE.Vector2()
		mouse.x = (adjMouseX / rendererRect.width) * 2 - 1;
		mouse.y = -((adjMouseY / rendererRect.height) * 2 - 1);
	
		// Get the picking ray from the point
		this.raycaster.setFromCamera(mouse, this.camera);

		// Find out if there's a hit
		if (Array.isArray(objects)) {
			return this.raycaster.intersectObjects(objects);
		}
		return this.raycaster.intersectObject(objects);
	}



	private createWallBody(x: number, y: number, z: number): CANNON.Body {
		return new CANNON.Body({
			mass: 0,
			position: new CANNON.Vec3(x, y, z),
			collisionFilterGroup: ShapeAnimation.physicsGroups.walls // Put into a group
		});
	}

	private createPhysicalRoom() {
		const size = 10;
		// Create room in THREE
		this.room = new THREE.Mesh(
			new THREE.BoxGeometry(10, 10, 10),
			new THREE.MeshBasicMaterial({color: 0xff0000, side: THREE.DoubleSide, wireframe: true})
		);
		this.room.layers.disableAll(); // Comment this out to render the "room"
		this.scene.add(this.room);
		
		// Floor
		let wallBody = this.createWallBody(0, 0 - size / 2, 0);
        wallBody.addShape(new CANNON.Plane());
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.floor = wallBody;
		
		// Ceiling
		wallBody = this.createWallBody(0, size / 2, 0);
        wallBody.addShape(new CANNON.Plane());
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.ceiling = wallBody;

		// Left
		wallBody = this.createWallBody(0 - size / 2, 0, 0);
        wallBody.addShape(new CANNON.Plane());
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.left = wallBody;

		// Right
		wallBody = this.createWallBody(size / 2, 0, 0);
        wallBody.addShape(new CANNON.Plane());
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.right = wallBody;
		
		// Back
		wallBody = this.createWallBody(0, 0, 0 - size / 2);
        wallBody.addShape(new CANNON.Plane());
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.back = wallBody;
		
		// Front
		wallBody = this.createWallBody(0, 0, size / 2);
        wallBody.addShape(new CANNON.Plane());
        wallBody.quaternion.setFromEuler(0, 0 - Math.PI, 0);
        this.physicsWorld.addBody(wallBody);
		this.roomsPhysicalWalls.front = wallBody;

		// Setup pushing off of walls (sides and front)
		const sideWallImpulseMin = 80;
		const sideWallImpulseMax = 100;
		this.roomsPhysicalWalls.ceiling.addEventListener("collide",
			(event: CannonCollideEvent) => this.onWallCollide( event, new CANNON.Vec3(0, randomNumber(-sideWallImpulseMax, -sideWallImpulseMin), 0) )
		);
		this.roomsPhysicalWalls.floor.addEventListener("collide",
			(event: CannonCollideEvent) => this.onWallCollide( event, new CANNON.Vec3(0, randomNumber(sideWallImpulseMin, sideWallImpulseMax), 0) )
		);
		this.roomsPhysicalWalls.left.addEventListener("collide",
			(event: CannonCollideEvent) => this.onWallCollide( event, new CANNON.Vec3(randomNumber(sideWallImpulseMin, sideWallImpulseMax), 0, 0) )
		);
		this.roomsPhysicalWalls.right.addEventListener("collide",
			(event: CannonCollideEvent) => this.onWallCollide( event, new CANNON.Vec3(randomNumber(-sideWallImpulseMax, -sideWallImpulseMin), 0, 0) )
		);
		this.roomsPhysicalWalls.front.addEventListener("collide",
			(event: CannonCollideEvent) => this.onWallCollide( event, new CANNON.Vec3(0, 0, randomNumber(-600, -400)) )
		);

		// Create plane which flat circles are placed against. Not part of the "room", but needed later.
		this.planeForFlatCircles = new THREE.Mesh(
			new THREE.PlaneGeometry(1, 1),
			new THREE.MeshBasicMaterial({color: 0x00ff00, side: THREE.DoubleSide, wireframe: true})
		);
		this.planeForFlatCircles.layers.disableAll(); // Comment this out to render the plane
		this.scene.add(this.planeForFlatCircles);
	}

	private onWallCollide = (
		event: CannonCollideEvent,
		impulse: CANNON.Vec3
	) => {
		const physicsBody = event.body;
		if (physicsBody !== undefined) {
			
			let mesh: THREE.Mesh;
			for (let i=0; i < this.shapes.length; i++) {
				if (this.shapes[i].physicsBody === physicsBody) {
					mesh = this.shapes[i].mesh;
					break;
				}
			}

			if (mesh !== undefined) {
				if (mesh.geometry.type === "TorusGeometry") {
					// Requires a lighter touch for some reason.
					impulse = impulse.scale(0.07);
				}

				const meshSize = (new THREE.Box3().setFromObject(mesh)).getSize(new THREE.Vector3());
				let relativePoint: CANNON.Vec3;
				if (mesh.geometry.type === "TorusGeometry") {
					relativePoint = new CANNON.Vec3(
						(randomNumber(-1, 1) > 0 ? 1 : -1) * meshSize.x / 2 * .5,
						(randomNumber(-1, 1) > 0 ? 1 : -1) * meshSize.y / 2 * .5,
						0
					);
				} else {
					relativePoint = new CANNON.Vec3(
						randomNumber(0 - meshSize.x / 2 * .33, meshSize.x / 2 * .33),
						randomNumber(0 - meshSize.y / 2 * .33, meshSize.y / 2 * .33),
						0
					);
				}

				physicsBody.applyImpulse(impulse, relativePoint);
			}
		}
	}

	private applyScale(meshOrPhysicsBody: THREE.Mesh|CANNON.Body, scale: number) {
		if (meshOrPhysicsBody === undefined) return;

		if (meshOrPhysicsBody instanceof THREE.Mesh) {
			meshOrPhysicsBody.scale.setScalar(scale);
		} else {
			meshOrPhysicsBody.shapes.forEach((shape: CANNON.Shape) => {
				if (shape instanceof CANNON.Trimesh) {
					shape.setScale(new CANNON.Vec3(scale,scale,scale));
				} else if (shape instanceof CANNON.Sphere) {
					if ((shape as any).userData === undefined) {
						(shape as any).userData = {};
					}
					if ((shape as any).userData.originalRadius === undefined) {
						(shape as any).userData.originalRadius = shape.radius
					}
					
					const originalRadius = (shape as any).userData.originalRadius;
	
					shape.radius = originalRadius * scale;
					shape.updateBoundingSphereRadius();
				}
			});
			
			meshOrPhysicsBody.updateAABB();
			meshOrPhysicsBody.updateMassProperties();
			meshOrPhysicsBody.updateBoundingRadius();
		}
	}
	

	private addObjectsToScene() {
		if (this.shapes === undefined) {
			this.shapes = new Array();
		}
		if (this.flatCircles === undefined) {
			this.flatCircles = new Array();
		}

		// Spheres
		this.createSphereWithPhsyics(1.10, this.horizontalStripesMaterial);
		this.createSphereWithPhsyics(0.95, this.verticalStripesMaterial);
		this.createSphereWithPhsyics(0.95, this.verticalStripesMaterial);
		this.createSphereWithPhsyics(0.75, this.horizontalStripesMaterial);
		this.createSphereWithPhsyics(0.75, this.horizontalStripesMaterial);

		// Toruses
		const torusRadius = 0.55;
		const torusRadiusTubeRatio = 0.525;
		this.createTorusWithPhsyics(torusRadius, torusRadius * torusRadiusTubeRatio, this.verticalStripesMaterial);
		this.createTorusWithPhsyics(torusRadius, torusRadius * torusRadiusTubeRatio, this.verticalStripesMaterial);

		// 2D Circles
		this.flatCirclePositions.forEach(() => {
			this.createFlatCircle(1);
		});
	}

	private createSphereWithPhsyics(radius: number, material: THREE.Material) {
		// THREE (webgl framework)
		const mesh = ShapeUtils.createSphere(radius, material);
		const outlineMesh = new THREE.Mesh(
			new THREE.SphereGeometry(radius, 32, 25),
			this.outlineMaterial
		);
		outlineMesh.visible = false;

		// Cannon (physics engine)
        let physicsBody = new CANNON.Body({
			mass: 6,
			collisionFilterGroup: ShapeAnimation.physicsGroups.spheres, // Put into a group
			collisionFilterMask:  // What groups it can collide with
				ShapeAnimation.physicsGroups.walls
				| ShapeAnimation.physicsGroups.spheres
				| ShapeAnimation.physicsGroups.toruses
		});
        physicsBody.addShape(new CANNON.Sphere(radius));

		const impulse = new CANNON.Vec3(
			randomNumber(-50, 50),
			randomNumber(-50, 50),
			0
		);
        physicsBody.applyImpulse(impulse);
        physicsBody.linearDamping = 0.02;

		physicsBody.angularVelocity.set(
			randomNumber(1, 2),
			randomNumber(1, 2),
			randomNumber(0, 1)
		);
		physicsBody.angularDamping = 0.75;

		// Add to world and scene
		this.shapes.push({
			physicsBody,
			mesh,
			outlineMesh
		});
        this.physicsWorld.addBody(physicsBody);
		this.shapesContainer.add(mesh);
		this.scene.add(outlineMesh);
	}

	private createTorusWithPhsyics(radius: number, radiusOfTube: number, material: THREE.Material) {
		// THREE (webgl framework)
		const mesh = ShapeUtils.createTorus(radius, radiusOfTube, material);
		const outlineMesh = new THREE.Mesh(mesh.geometry, this.outlineMaterial);
		outlineMesh.visible = false;

		// Cannon (physics engine)
        const physicsBody = new CANNON.Body({
			mass: 6,
			collisionFilterGroup: ShapeAnimation.physicsGroups.toruses, // Put into a group
			collisionFilterMask:  // What groups it can collide with
				ShapeAnimation.physicsGroups.walls
				| ShapeAnimation.physicsGroups.spheres
				| ShapeAnimation.physicsGroups.toruses
		});
        physicsBody.addShape(
			this.createCannonTrimesh(
				new THREE.TorusGeometry(radius, radiusOfTube, 12, 20) // Simplified geometry
			)
		);

		const impulse = new CANNON.Vec3(
			randomNumber(-50, 50),
			randomNumber(-50, 50),
			0
		);
        physicsBody.applyImpulse(impulse);
        physicsBody.linearDamping = 0.02;

		physicsBody.angularVelocity.set(
			randomNumber(1, 2),
			randomNumber(1, 2),
			randomNumber(0, 1)
		);
		physicsBody.angularDamping = 0.75;

		// Add to world and scene
        this.physicsWorld.addBody(physicsBody);
		this.shapesContainer.add(mesh);
		this.scene.add(outlineMesh);


		//===============
		const radiusForSphere = radius + (radiusOfTube * 0.25); // Radius for a torus is the halfway point in the middle of the tube. Correct would be "radius + radiusOfTube" but the value used here gives us a general appearance of bouncing when two toruses get close to each other

		const showBoundaryGeometry = false;
		let meshSphere: THREE.Mesh = undefined;
		if (showBoundaryGeometry) {
			const geometrySphere = new THREE.SphereGeometry(radiusForSphere, 12, 12);
			meshSphere = new THREE.Mesh(
				geometrySphere,
				new THREE.MeshBasicMaterial({color: 0x0000ff, side: THREE.DoubleSide, wireframe: true})
			);
		}
		
		const physicsSphereBody = new CANNON.Body({
			mass: 1,
			collisionFilterGroup: ShapeAnimation.physicsGroups.torusesBounceAreas, // Put into a group
			collisionFilterMask:  // What groups it can collide with
				ShapeAnimation.physicsGroups.torusesBounceAreas
		});
        physicsSphereBody.addShape(new CANNON.Sphere(radiusForSphere));
        physicsSphereBody.linearDamping = 0;
        physicsSphereBody.angularDamping = 0;

		this.physicsWorld.addConstraint(new CANNON.LockConstraint(
			physicsBody,
			physicsSphereBody
		));
		
		this.shapes.push({
			physicsBody,
			mesh,
			outlineMesh,
			physicsBodySecondary: physicsSphereBody,
			meshSecondary: meshSphere
		});
        this.physicsWorld.addBody(physicsSphereBody);
		if (meshSphere !== undefined) {
			this.scene.add(meshSphere); // Note: Not putting in these.shapesContainer so raycaster does not select it.
		}
	}

	private createCannonTrimesh(geometry: THREE.BufferGeometry) {
		const vertices = geometry.attributes.position.array;
		const indices = Object.keys(vertices).map(Number);
		return new CANNON.Trimesh(vertices as [], indices)
	}

	private createFlatCircle(radius: number) {
		// THREE (webgl framework)
		const geometry = new THREE.CircleGeometry(radius, 64);
		const circle = new THREE.Mesh(geometry, this.solidYellowMaterial);

		circle.position.set(-10, 1.5, 0);
		
		this.flatCircles.push(circle);
		this.shapesContainer.add(circle);
	}


	protected updateAndRender = () => { // Arrow syntax use is to bind "this".
		if (this.isRendering() === false) {
			return;
		}

		// Update shape hover styling
		let isHoveringOverShape = false;
		if (this.shapes !== undefined) {
			this.shapes.forEach(meshAndBody => {
				meshAndBody.outlineMesh.visible = false;
			});
			if (this.pointerGrabObjectConstraintMesh === null && this.pointerPosition !== null) {
				const intersects = this.getPointerIntersectObjects(this.pointerPosition.x, this.pointerPosition.y);
				if (intersects.length > 0) {
					const intersect = intersects[0];
					const meshAndBody = this.shapes.find(entry => intersect.object === entry.mesh);
					if (meshAndBody !== undefined) {
						meshAndBody.outlineMesh.visible = true;
						isHoveringOverShape = true;
					}
				}
			}
		}

		// Update mouse cursor
		let newCursor = "auto";
		if (this.pointerGrabObjectConstraintMesh !== null) {
			newCursor = "grabbing";
		} else if (isHoveringOverShape) {
			newCursor = "grab";
		}
		if (this.renderer.domElement.style.cursor !== newCursor) {
			this.renderer.domElement.style.cursor = newCursor;
		}


		// Update if container has changed size (or font has not been rendered yet) or has not been rendered yet
		if (this.renderingTracker.isUpdateNeeded()) {
			// Set size of render
			const canvasSize = this.getCanvasSize();
			this.renderer.setSize(canvasSize.width, canvasSize.height);
			this.camera.aspect = canvasSize.width / canvasSize.height;

			// Scale and position "room"
			this.room.position.set(0, 0, 0);
			ShapeUtils.scaleToFillView(this.room, 20, this.camera, this.cameraStartPosition);

			const roomBox = new THREE.Box3().setFromObject(this.room);
			const roomSize = roomBox.getSize(new THREE.Vector3());
			this.room.position.z = 0 - roomSize.z * 0.5;

			// Position CANNON's walls to edges of the "room"
			this.roomsPhysicalWalls.floor.position.y	= 0 - roomSize.y / 2;
			this.roomsPhysicalWalls.ceiling.position.y	= roomSize.y / 2;
			this.roomsPhysicalWalls.left.position.x		= 0 - roomSize.x / 2;
			this.roomsPhysicalWalls.right.position.x	= roomSize.x / 2;
			this.roomsPhysicalWalls.back.position.z		= 0 - roomSize.z / 2 + this.room.position.z;
			this.roomsPhysicalWalls.front.position.z	= roomSize.z / 2 + this.room.position.z;

			const halfOfVerticleFOV		= THREE.MathUtils.degToRad(this.camera.getEffectiveFOV() / 2);
			const horiziontalFOV_inDeg 	= 2 * Math.atan( Math.tan( this.camera.fov * Math.PI / 180 / 2 ) * this.camera.aspect ) * 180 / Math.PI;
			const halfOfHorizontalFOV	= THREE.MathUtils.degToRad(horiziontalFOV_inDeg / 2);

			this.roomsPhysicalWalls.left.quaternion.setFromEuler(0, Math.PI / 2 + halfOfHorizontalFOV, 0);
			this.roomsPhysicalWalls.right.quaternion.setFromEuler(0, 0 - Math.PI / 2 - halfOfHorizontalFOV, 0);
			
			this.roomsPhysicalWalls.ceiling.quaternion.setFromEuler(Math.PI / 2 + halfOfVerticleFOV, 0, 0);
			this.roomsPhysicalWalls.floor.quaternion.setFromEuler(0 - Math.PI / 2 - halfOfVerticleFOV, 0, 0);


			const renderSideWallsForDebugging = false;
			if (renderSideWallsForDebugging) {
				var makeWallVisible = (physicsWall: CANNON.Body) => {
					const visibleWall = new THREE.Mesh(
						new THREE.PlaneGeometry(50, 50, 10, 10),
						new THREE.MeshBasicMaterial({color: 0xff0000, side: THREE.DoubleSide, wireframe: true})
					);
					visibleWall.position.set(physicsWall.position.x, physicsWall.position.y, physicsWall.position.z);
					visibleWall.quaternion.set(physicsWall.quaternion.x, physicsWall.quaternion.y, physicsWall.quaternion.z, physicsWall.quaternion.w);
					this.scene.add(visibleWall);
				}
				makeWallVisible(this.roomsPhysicalWalls.floor);
				makeWallVisible(this.roomsPhysicalWalls.ceiling);
				makeWallVisible(this.roomsPhysicalWalls.left);
				makeWallVisible(this.roomsPhysicalWalls.right);
			}


			// Create 
			this.planeForFlatCircles.position.z = this.room.position.z + (roomSize.z * 0.5 * 0.5);
			const {scalarWidth, scalarHeight} = ShapeUtils.scaleToFillView(this.planeForFlatCircles, 1, this.camera, this.cameraStartPosition);
			
			// Add objects to scene
			const responsiveScalar = (scalarWidth + scalarHeight) / 2 * 0.1;
			if (this.shapes === undefined || this.shapes.length === 0) {
				this.addObjectsToScene();
			}

			// Scale objects based on canvas size
			this.shapes.forEach(({ physicsBody, mesh, outlineMesh, physicsBodySecondary, meshSecondary}) => {
				this.applyScale(physicsBody, responsiveScalar);
				this.applyScale(mesh, responsiveScalar);
				this.applyScale(outlineMesh, responsiveScalar * this.outlineScalar);
				this.applyScale(physicsBodySecondary, responsiveScalar);
				this.applyScale(meshSecondary, responsiveScalar);
			});

			this.flatCircles.forEach((circle: THREE.Mesh) => {
				this.applyScale(circle, responsiveScalar);
			});

			// Position the flat cirlces inside the "room"
			const planeForFlatCirclesSize = (new THREE.Box3().setFromObject(this.planeForFlatCircles)).getSize(new THREE.Vector3());
			for (let i=0; i < this.flatCircles.length; i++ ) {
				const circle = this.flatCircles[i];
				const screenPosition = this.flatCirclePositions[i].screen;
				const offsetPosition = this.flatCirclePositions[i].offset;
				if (screenPosition === undefined) {
					break;
				}

				const circleSize = new THREE.Box3().setFromObject(circle).getSize(new THREE.Vector3());

				circle.position.x = (planeForFlatCirclesSize.x - circleSize.x) / 2  * screenPosition.x;
				circle.position.y = (planeForFlatCirclesSize.y - circleSize.y) / 2  * screenPosition.y;
				circle.position.z = this.planeForFlatCircles.position.z;
				if (offsetPosition !== undefined) {
					if (screenPosition.x !== undefined) circle.position.x += offsetPosition.x * circleSize.x;
					if (screenPosition.y !== undefined) circle.position.y += offsetPosition.y * circleSize.y;
				}
			}

			// Randomize position of objects inside the "room", if they have not been positined before
			this.shapes.forEach(({physicsBody, mesh}) => {
				if (mesh.userData.isInitPositioned !== true) {
					const meshSize = new THREE.Box3().setFromObject(mesh).getSize(new THREE.Vector3());
					physicsBody.position.x = randomNumber(0 - roomSize.x / 2 + meshSize.x, roomSize.x / 2 - meshSize.x);
					physicsBody.position.y = randomNumber(0 - roomSize.y / 2 + meshSize.y, roomSize.y / 2 - meshSize.y);
					physicsBody.position.z = randomNumber(
						(0 - roomSize.z / 2 + this.room.position.z + meshSize.z) * 0.25,
						(roomSize.z / 2 + this.room.position.z - meshSize.z) * 0.25
					);
					mesh.userData.isInitPositioned = true;
				}
			});

			// Update camera's projection matrix, and reset the boolean
			this.camera.updateProjectionMatrix();
			this.renderingTracker.setUpdateNeeded(false);
		}

		// Step the physics world
        this.physicsWorld.fixedStep()

		// Update meshes based on their physics bodies
		this.shapes.forEach(({ physicsBody, mesh, outlineMesh, physicsBodySecondary, meshSecondary}) => {
			mesh.position.set(physicsBody.position.x, physicsBody.position.y, physicsBody.position.z);
			mesh.quaternion.set(physicsBody.quaternion.x, physicsBody.quaternion.y, physicsBody.quaternion.z, physicsBody.quaternion.w);

			outlineMesh.position.copy(mesh.position);
			outlineMesh.quaternion.copy(mesh.quaternion);

			// TODO: Delete next 2 lines
			// const tempSize = new THREE.Box3().setFromObject(outlineMesh).getSize(new THREE.Vector3());
			// outlineMesh.position.z -= tempSize.z / 2;

			if (meshSecondary !== undefined) {
				meshSecondary.position.copy(mesh.position);
				meshSecondary.quaternion.copy(mesh.quaternion);
			}
			/* This is not needed.
			if (physicsBodySecondary !== undefined) {
				physicsBodySecondary.position.copy(physicsBody.position);
				physicsBodySecondary.quaternion.copy(physicsBody.quaternion);
			}
			*/
		});

		// Render
		this.renderer.render(this.scene, this.camera);
	}
}



